<template>
  <div class="lasttime-container">
    <!-- Skeleton Loader -->
    <div v-if="loading" class="lasttime-skeleton">
      <div v-for="i in 5" :key="i" class="skeleton-card">
        <div class="skeleton-image"></div>
        <div class="skeleton-content">
          <div class="skeleton-category"></div>
          <div class="skeleton-title"></div>
          <div class="skeleton-date"></div>
        </div>
      </div>
    </div>

    <!-- Lista horizontal -->
    <div v-else-if="posts.length > 0" class="lasttime-scroll">
      <div 
        v-for="post in posts" 
        :key="post.id"
        class="lasttime-card"
        @click="goToPost(post)"
      >
        <img :src="getImageUrl(post.image)" :alt="post.title" class="lasttime-image">
        <div class="lasttime-content">
          <span class="lasttime-category">{{ post.category?.name || 'Noticia' }}</span>
          <h3 class="lasttime-title">{{ post.title }}</h3>
          <span class="lasttime-date">{{ formatDate(post.created_at) }}</span>
        </div>
      </div>
    </div>

    <div v-else class="lasttime-empty">
      No hay noticias disponibles
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '../services/api'

const router = useRouter()
const posts = ref([])
const loading = ref(true)

const getImageUrl = (image) => {
  if (!image) return '/noimagen.png'
  if (image.startsWith('http')) return image
  return `https://portal.jlhvision.com/posts/${image}`
}

const formatDate = (date) => {
  if (!date) return ''
  const d = new Date(date)
  const now = new Date()
  const diff = Math.floor((now - d) / 1000 / 60 / 60) // horas
  
  if (diff < 1) return 'Hace unos minutos'
  if (diff < 24) return `Hace ${diff} horas`
  if (diff < 48) return 'Ayer'
  return d.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short'
  })
}

const loadLastTimePosts = async () => {
  loading.value = true
  try {
    const response = await api.get('/lasttime')
    // 🔥 CORREGIDO: response.data es directamente el array
    posts.value = response.data || []
  } catch (error) {
    console.error('Error cargando últimas noticias:', error)
    posts.value = []
  } finally {
    loading.value = false
  }
}

const goToPost = (post) => {
  router.push(`/noticia/${post.slug}`)
}

onMounted(() => {
  loadLastTimePosts()
})
</script>

<style scoped>
.lasttime-container {
  width: 100%;
  margin: 15px 0;
}

/* Scroll horizontal - SIN barra visible */
.lasttime-scroll {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  padding: 4px 2px 8px 2px;
  
  /* 🔥 Ocultar scrollbar en todos los navegadores */
  -ms-overflow-style: none;  /* IE y Edge */
  scrollbar-width: none;     /* Firefox */
}

/* Ocultar scrollbar en Chrome, Safari y Opera */
.lasttime-scroll::-webkit-scrollbar {
  display: none;
}

/* Card */
.lasttime-card {
  flex: 0 0 280px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.lasttime-card:active {
  transform: scale(0.98);
}

.lasttime-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
  object-position: center;
}

.lasttime-content {
  padding: 12px;
}

.lasttime-category {
  display: inline-block;
  background: #a50000;
  color: white;
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 12px;
  margin-bottom: 8px;
  text-transform: uppercase;
  font-weight: bold;
}

.lasttime-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 8px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.lasttime-date {
  font-size: 11px;
  color: #999;
}

/* Skeleton */
.lasttime-skeleton {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 4px 2px 8px 2px;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.lasttime-skeleton::-webkit-scrollbar {
  display: none;
}

.skeleton-card {
  flex: 0 0 280px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.skeleton-image {
  width: 100%;
  height: 150px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-content {
  padding: 12px;
}

.skeleton-category {
  width: 60px;
  height: 18px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
  margin-bottom: 8px;
}

.skeleton-title {
  width: 90%;
  height: 16px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 6px;
}

.skeleton-title:nth-child(2) {
  width: 70%;
}

.skeleton-date {
  width: 50px;
  height: 12px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.lasttime-empty {
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 14px;
}

/* Responsive */
@media (max-width: 480px) {
  .lasttime-card {
    flex: 0 0 240px;
  }
  
  .lasttime-image {
    height: 130px;
  }
  
  .lasttime-title {
    font-size: 13px;
  }
}
</style>